<script setup lang="ts" generic="StepData extends { navigation?: boolean }">
type StepDefs<data> = Record<number, data>

interface Props {
  modelValue: number
  steps: StepDefs<StepData>
  emitedNavigation?: boolean
  submitLabel?: string
  finalizeAcction?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitLabel: 'Finalizar',
  finalizeAcction: true,
})

const emits = defineEmits(['update:modelValue', 'nextStep', 'previousStep', 'end'])
const { modelValue: model } = useVModels(props, emits)

const { LocalConfigurationStore } = stores()
const goToStep = (step: number) => {
  model.value = step
}

const isFirstStep = computed(() => {
  const firstStep = Object.keys(props.steps)[0]
  return firstStep === model.value.toString()
})

const isLastStep = computed(() => {
  const stepsList = Object.keys(props.steps)
  const lasteStep = stepsList[stepsList.length - 1]
  return lasteStep === model.value.toString()
})

const nextStep = () => {
  if (props.emitedNavigation) emits('nextStep')
  else {
    const stepsList = Object.keys(props.steps)
    const value = stepsList[stepsList.indexOf(`${model.value}`) + 1]
    if (value) model.value = +value
  }
}

const previousStep = () => {
  if (props.emitedNavigation) emits('previousStep')
  else {
    const stepsList = Object.keys(props.steps)
    const value = stepsList[stepsList.indexOf(`${model.value}`) - 1]
    if (value) model.value = +value
  }
}
const isDevelopment = computed(() => process.env['NODE_ENV'] === 'development')
</script>

<template>
  <PvStepper v-model:value="model">
    <div class="grid gap-4">
      <PvStepList>
        <template
          v-for="(_, step, index) in steps"
          :key="step"
        >
          <PvStep
            as-child
            :value="step"
          >
            <div
              v-if="+step !== Math.min(...Object.keys(steps).map((step) => +step))"
              class="mx-2 w-full"
            >
              <PvDivider />
            </div>
            <button @click="() => (isDevelopment ? goToStep(+step) : undefined)">
              <span
                :class="[
                  'inline-flex size-6 items-center justify-center rounded-full leading-none font-bold',
                  {
                    'bg-primary text-white': +step <= model,
                    'bg-surface-500 dark:bg-surface-50 text-surface-50 dark:text-surface-500':
                      +step > model,
                  },
                ]"
              >
                {{ index + 1 }}
              </span>
            </button>
          </PvStep>
        </template>
      </PvStepList>
      <PvStepPanels>
        <template
          v-for="(data, step) in steps"
          :key="step"
        >
          <PvStepPanel
            :value="+step"
            class="grid gap-10"
          >
            <div class="grid gap-6">
              <slot
                :name="`step-${step}`"
                :step="step"
                :data="data"
              />
            </div>
            <div
              v-if="!isFirstStep || data.navigation !== false"
              class="flex justify-between"
            >
              <div>
                <PvButton
                  v-if="!isFirstStep"
                  severity="contrast"
                  outlined
                  @click="previousStep()"
                >
                  <span class="icon-[ph--caret-double-left-bold]" />
                  Regresar
                </PvButton>
              </div>

              <PvButton
                v-if="data.navigation !== false && (finalizeAcction || !isLastStep)"
                :severity="isLastStep ? 'success' : undefined"
                :style="{
                  backgroundColor: LocalConfigurationStore?.brokerContext?.preset?.accentColorHex,
                }"
                @click="isLastStep ? emits('end') : nextStep()"
              >
                <span class="flex items-center gap-2 font-bold">
                  <span
                    v-if="isLastStep"
                    class="icon-[ph--shield-check-bold]"
                  />
                  {{ isLastStep ? (submitLabel ?? 'Enviar') : 'Continuar' }}
                  <span
                    v-if="!isLastStep"
                    class="icon-[ph--caret-double-right-bold]"
                  />
                </span>
              </PvButton>
            </div>
          </PvStepPanel>
        </template>
      </PvStepPanels>
    </div>
  </PvStepper>
</template>
